import { gql, useMutation } from '@apollo/client';
import { LoadingButton } from '@mui/lab';
import { Grid, Typography } from '@mui/material';
import Fieldset from 'components/Fieldset';
import { Select } from 'components/Inputs/Select';
import Selection from 'components/Inputs/Selection';
import TextField from 'components/Inputs/TextField';
import Section from 'components/Section';
import { useUser } from 'contexts/userContext';
import {
  SUBMIT_END_YEAR_REVIEW,
  SubmitEndYearReviewResponse,
  SubmitEndYearReviewVariables,
  UPDATE_END_YEAR_REVIEW,
  UpdateEndYearReviewResponse,
  UpdateEndYearReviewVariables,
} from 'graphql/mutation/userGoals';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { RecommendedForPromotionEnum, User, UserGoal, UserRatingEnum, UserStatusEnum } from 'types';

type RowType = {
  _id: string;
  user?: User;
  empCommentEndYear?: string;
  managerCommentEndYear?: string;
  empRatingEndYear?: string;
  managerRatingEndYear?: string;
  overallCommentByEmployee?: string;
  overallCommentByManager?: string;
};

const USER_PROMOTION_ANSWER = gql`
  mutation UserPromotionAnswer($inputs: [UserQAInput]) {
    userPromotionAnswer(inputs: $inputs) {
      _id
      desc
    }
  }
`;
type UserPromotionAnswerRespone = {
  userPromotionAnswer: {
    _id: string;
    desc: string;
  };
};
type UserPromotionAnswerVariables = {
  inputs: {
    _id?: string;
    desc: string;
    qa?: string;
    userId?: string;
  }[];
};

const EndYearEvaluationForm: React.FC<{ initialRows: UserGoal[]; employee: User }> = ({
  initialRows,
  employee,
}) => {
  const [rows, setRows] = useState<RowType[]>(initialRows);
  const [userFormState, setUserFormState] = useState<{
    overallCommentByEmployee: string;
    overallCommentByManager: string;
    isRecommendedForPromotion: RecommendedForPromotionEnum;
  }>({
    overallCommentByEmployee: employee.annualUserInfo.overallCommentByEmployee ?? '',
    overallCommentByManager: employee.annualUserInfo.overallCommentByManager ?? '',
    isRecommendedForPromotion:
      employee.annualUserInfo.isRecommendedForPromotion ?? RecommendedForPromotionEnum.NO,
  });

  const [promotionAnswers, setPromotionAnswers] = useState(
    employee.promotionQuestions.map((q, idx) => ({
      _id: q.answer?._id ?? idx,
      desc: q.answer?.desc ?? '',
      user: employee._id,
      qa: q._id,
    }))
  );

  const isMobileScreen = false;

  const { user: loggedInUser } = useUser();

  const [updateEndYearUserGoals, { loading: updatingMidYearUserGoals }] = useMutation<
    UpdateEndYearReviewResponse,
    UpdateEndYearReviewVariables
  >(UPDATE_END_YEAR_REVIEW);

  const [submitEndYearUserGoals, { loading: submittingUserGoals }] = useMutation<
    SubmitEndYearReviewResponse,
    SubmitEndYearReviewVariables
  >(SUBMIT_END_YEAR_REVIEW);

  const [updatePromotionAnswer, { loading: updatingPromotionAnswer }] = useMutation<
    UserPromotionAnswerRespone,
    UserPromotionAnswerVariables
  >(USER_PROMOTION_ANSWER);

  const navigate = useNavigate();

  const isManager = loggedInUser?._id === employee.reportingManager?._id;
  const isThemself = loggedInUser?._id === employee._id;
  const isUserSaved =
    employee.annualUserInfo?.endYearStatus === UserStatusEnum.DRAFT ||
    employee.annualUserInfo?.endYearStatus === UserStatusEnum['NOT STARTED'];
  const isAdmin = loggedInUser?.role.name === 'ADMIN';

  const isApproved = employee.annualUserInfo?.endYearStatus === UserStatusEnum.APPROVED;
  const promotionQuestions = employee.promotionQuestions;

  const shouldEnableEdit = useMemo(() => {
    if (isManager) {
      switch (employee.annualUserInfo?.endYearStatus) {
        case UserStatusEnum['PENDING FOR APPROVAL']:
          return true;
        default:
          return false;
      }
    } else if (isThemself) {
      switch (employee.annualUserInfo?.endYearStatus) {
        case UserStatusEnum.DRAFT:
        case UserStatusEnum['NOT STARTED']:
          return true;
        default:
          return false;
      }
    } else if (isAdmin) {
      switch (employee.annualUserInfo?.startYearStatus) {
        case UserStatusEnum.DRAFT:
        case UserStatusEnum['NOT STARTED']:
          return false;
        default:
          return true;
      }
    }
    return false;
  }, [isManager, isThemself, employee.annualUserInfo?.endYearStatus, isAdmin]);

  const handlePromotionAnswerChange = (index: number, value: string) => {
    setPromotionAnswers(prev =>
      prev.map((a, currIdx) => {
        if (currIdx === index) {
          return {
            ...a,
            desc: value,
          };
        }
        return a;
      })
    );
  };

  const validateSubmitFields = () => {
    for (let idx = 0; idx < rows.length; idx++) {
      const r = rows[idx];
      if (isThemself) {
        if (!!!r.empCommentEndYear || !!!r.empRatingEndYear) {
          return false;
        }
      } else {
        if (!!!r.managerCommentEndYear || !!!r.managerRatingEndYear) {
          return false;
        }
      }
    }

    if (isThemself && !!!userFormState.overallCommentByEmployee) return false;
    else if (!isThemself) {
      if (!!!userFormState.overallCommentByManager) return false;
      if (userFormState.isRecommendedForPromotion === RecommendedForPromotionEnum.YES)
        if (
          promotionAnswers.find(a => {
            if (!!!a.desc) return true;
          })
        )
          return false;
    }
    return true;
  };

  const shouldDisableSubmit = useMemo(() => {
    if (validateSubmitFields()) {
      if (isThemself) {
        switch (employee.annualUserInfo?.endYearStatus) {
          case UserStatusEnum.DRAFT:
          case UserStatusEnum['NOT STARTED']:
            return false;
          default:
            return true;
        }
      } else if (
        !isThemself &&
        employee.annualUserInfo?.endYearStatus !== UserStatusEnum.APPROVED
      ) {
        return false;
      } else if (isAdmin) {
        switch (employee.annualUserInfo?.startYearStatus) {
          case UserStatusEnum.DRAFT:
          case UserStatusEnum['NOT STARTED']:
            return true;
          default:
            return false;
        }
      }
    }
    return true;
  }, [employee, loggedInUser, rows, isThemself, userFormState, promotionAnswers, isAdmin]);

  const handleSaveUserReviews = () => {
    const refinedRows = rows.map(currRow => {
      return {
        userId: employee._id,
        userGoalId: currRow._id,
        empCommentEndYear: !!currRow.empCommentEndYear ? currRow.empCommentEndYear : undefined,
        managerCommentEndYear: !!currRow.managerCommentEndYear
          ? currRow.managerCommentEndYear
          : undefined,
        empRatingEndYear: !!currRow.empRatingEndYear ? currRow.empRatingEndYear : undefined,
        managerRatingEndYear: !!currRow.managerRatingEndYear
          ? currRow.managerRatingEndYear
          : undefined,
      } as UpdateEndYearReviewVariables['input']['userGoals']['0'];
    });

    updateEndYearUserGoals({
      variables: {
        input: {
          userGoals: refinedRows,
          overallCommentByEmployee: userFormState.overallCommentByEmployee,
          overallCommentByManager: userFormState.overallCommentByManager,
          isRecommendedForPromotion: userFormState.isRecommendedForPromotion,
          userId: employee._id,
        },
      },
      onCompleted: res => {
        if (userFormState.isRecommendedForPromotion === RecommendedForPromotionEnum.YES) {
          updatePromotionAnswer({
            variables: {
              inputs: promotionAnswers.map(ans => {
                const refinedAns = {
                  _id: ans._id,
                  desc: ans.desc,
                  userId: employee._id,
                  qa: ans.qa,
                };
                if (typeof ans._id === 'number') {
                  return {
                    ...refinedAns,
                    _id: undefined,
                  };
                } else {
                  return refinedAns;
                }
              }),
            },
            onCompleted: _ => {
              toast.success('Saved Successfully');
            },
          });
        } else toast.success('Saved Successfully');
        setRows(res.updateEndYearUserGoals.userGoals);
      },
    });
  };

  const handleSubmitEndYearReviews = () => {
    const refinedRows = rows.map(currRow => {
      return {
        userId: employee._id,
        userGoalId: currRow._id,
        empCommentEndYear: currRow.empCommentEndYear,
        empRatingEndYear: currRow.empRatingEndYear,
        overallCommentByEmployee: currRow.overallCommentByEmployee,
      } as SubmitEndYearReviewVariables['input']['userGoals']['0'];
    });

    submitEndYearUserGoals({
      variables: {
        input: {
          userGoals: refinedRows,
          overallCommentByEmployee: userFormState.overallCommentByEmployee,
          isRecommendedForPromotion: userFormState.isRecommendedForPromotion,
        },
      },
      onCompleted: ugRes => {
        setRows(ugRes.submitEndYearUserGoals.userGoals);
        navigate(-1);
        toast.success('Your end year review have been submitted successfully');
      },
    });
  };

  return (
    <form>
      <Grid container direction="column" rowGap={2.5} mb={14} position="relative">
        {rows.map((item, idx) => (
          <ObjectiveLineItem
            idx={idx}
            userGoal={item as UserGoal}
            setRows={setRows}
            rows={rows}
            key={idx}
            editable={shouldEnableEdit}
            employee={employee}
          />
        ))}
        <Grid item xs={12}>
          <TextField
            value={userFormState.overallCommentByEmployee}
            onChange={e =>
              setUserFormState(prev => ({ ...prev, overallCommentByEmployee: e.target.value }))
            }
            disabled={!isThemself || !isUserSaved}
            label="Overall comment by Employee"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            value={userFormState.overallCommentByManager}
            disabled={
              isThemself ||
              (isManager && employee.annualUserInfo?.endYearStatus === UserStatusEnum.APPROVED)
            }
            onChange={e =>
              setUserFormState(prev => ({ ...prev, overallCommentByManager: e.target.value }))
            }
            label="Overall comment by Manager"
          />
        </Grid>
        {!isThemself && (
          <>
            <Fieldset label="is Recommended for Promotion?">
              <Grid item xs={12} container rowGap={1.5} columnSpacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <Selection
                    label="Yes"
                    required
                    disabled={isApproved}
                    activeId={userFormState.isRecommendedForPromotion}
                    handleChange={val =>
                      setUserFormState(prev => ({ ...prev, isRecommendedForPromotion: val }))
                    }
                    id={RecommendedForPromotionEnum.YES}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Selection
                    label="No"
                    required
                    disabled={isApproved}
                    activeId={userFormState.isRecommendedForPromotion}
                    handleChange={val =>
                      setUserFormState(prev => ({ ...prev, isRecommendedForPromotion: val }))
                    }
                    id={RecommendedForPromotionEnum.NO}
                  />
                </Grid>
              </Grid>
            </Fieldset>
            {userFormState.isRecommendedForPromotion === RecommendedForPromotionEnum.YES && (
              <Grid item xs={12} container rowGap={1.5} columnSpacing={1.5}>
                {promotionAnswers.map((currAns, idx) => (
                  <Grid item container direction={'column'} key={idx}>
                    <Grid item display={'flex'} columnGap={1}>
                      <Typography variant="subtitle1">Q:</Typography>
                      <Typography variant="subtitle1">{promotionQuestions[idx].desc}</Typography>
                    </Grid>
                    <Grid item display={'flex'} columnGap={1}>
                      <Typography variant="subtitle1">A:</Typography>
                      <TextField
                        disabled={
                          isThemself ||
                          (isManager &&
                            employee.annualUserInfo?.endYearStatus === UserStatusEnum.APPROVED)
                        }
                        sx={{ width: isMobileScreen ? '100%' : '100%' }}
                        multiline
                        minRows={2}
                        value={currAns.desc ?? ''}
                        onChange={e => handlePromotionAnswerChange(idx, e.target.value)}
                      />
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}
        <Grid
          container
          display="flex"
          justifyContent="center"
          alignItems="center"
          position="sticky"
          bottom={0}
          right={0}
          left={0}
          py={3}
          px={3}
          bgcolor="white"
          zIndex={10}
          sx={{
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
          }}
          boxShadow={2}
          rowGap={2}
        >
          <Grid container item direction={'column'} xs={12} md={6} lg={8}>
            <Typography color={'crimson'} fontWeight={600}>
              {(isThemself ? '*Employee rating & Employee' : '*Manager rating & Manager') +
                ' End Year Review are required to be filled in order to Submit.'}
            </Typography>
          </Grid>
          <Grid
            item
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            columnGap={1.5}
            xs={12}
            md={6}
            lg={4}
          >
            {((isManager &&
              employee.annualUserInfo?.endYearStatus === UserStatusEnum['PENDING FOR APPROVAL']) ||
              (isThemself && isUserSaved) ||
              (isAdmin && !isUserSaved)) && (
              <LoadingButton
                variant="contained"
                onClick={handleSaveUserReviews}
                loading={updatingMidYearUserGoals}
                disabled={
                  employee.annualUserInfo?.endYearStatus === UserStatusEnum.APPROVED ||
                  (!isThemself && shouldDisableSubmit)
                }
              >
                Save
              </LoadingButton>
            )}
            {isThemself && (
              <LoadingButton
                variant="contained"
                disabled={shouldDisableSubmit}
                loading={submittingUserGoals}
                onClick={handleSubmitEndYearReviews}
              >
                Submit
              </LoadingButton>
            )}
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
};

const ObjectiveLineItem: React.FC<{
  idx: number;
  userGoal: UserGoal;
  rows: RowType[];
  editable: boolean;
  setRows: React.Dispatch<React.SetStateAction<RowType[]>>;
  employee: User;
}> = ({ userGoal, setRows, idx, editable, employee }) => {
  const { user: loggedInUser } = useUser();
  const isThemself = loggedInUser?._id === employee._id;
  const handleChange = (fieldName: string, value: any) => {
    setRows(prev =>
      prev.map(curr => {
        if (curr._id === userGoal._id) {
          return { ...curr, [fieldName]: value };
        } else return curr;
      })
    );
  };

  return (
    <Grid item>
      <Section title={'Serial No. ' + (idx + 1).toString()} collapsible>
        <Grid container rowGap={1.5} columnSpacing={1.5} alignItems="center" px={0.5} py={0.5}>
          <Grid item xs={12}>
            <TextField
              size="small"
              multiline
              minRows={2}
              label="KRA"
              disabled
              value={userGoal.kra ?? ''}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              size="small"
              multiline
              disabled
              minRows={2}
              label="KPI"
              value={userGoal.kpi ?? ''}
              onChange={e => handleChange('kpi', e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              size="small"
              label="Target"
              disabled
              value={userGoal.target ?? ''}
              onChange={e => handleChange('target', e.target.value ?? '')}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField size="small" label="Weightage" disabled value={userGoal.weightage ?? ''} />
          </Grid>
          <Grid item xs={12}>
            <TextField
              multiline
              disabled
              value={userGoal.empCommentMidYear ?? ''}
              label="Employee Mid Year Review"
              onChange={e => handleChange('empCommentMidYear', e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              multiline
              disabled
              value={userGoal.managerCommentMidYear ?? ''}
              label="Manager Mid Year Review"
              onChange={e => handleChange('managerCommentMidYear', e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              multiline
              disabled={!editable || !isThemself}
              value={userGoal.empCommentEndYear ?? ''}
              label="Employee End Year Review"
              onChange={e => handleChange('empCommentEndYear', e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              multiline
              disabled={isThemself}
              value={userGoal.managerCommentEndYear ?? ''}
              label="Manager End Year Review"
              onChange={e => handleChange('managerCommentEndYear', e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={6}>
            <Select
              label="Employee Rating"
              value={userGoal.empRatingEndYear ?? ''}
              disabled={!editable || !isThemself}
              options={Object.keys(UserRatingEnum).map(k => ({
                label: k,
                value: UserRatingEnum[k],
              }))}
              onChange={val => handleChange('empRatingEndYear', val)}
            />
          </Grid>
          {!isThemself && (
            <Grid item xs={6}>
              <Select
                label="Manager Rating"
                value={userGoal.managerRatingEndYear ?? ''}
                options={Object.keys(UserRatingEnum).map(k => ({
                  label: k,
                  value: UserRatingEnum[k],
                }))}
                onChange={val => handleChange('managerRatingEndYear', val)}
              />
            </Grid>
          )}
        </Grid>
      </Section>
    </Grid>
  );
};

export default EndYearEvaluationForm;
